import { Fragment, useState, forwardRef, useRef, useEffect } from "react";
import HttpService from "../services/HttpService";
import {
  Container,
  Grid,
  Button,
  Alert as MuiAlert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import uploadImg from "./../images/upload.svg";
import FileUpload from "react-mui-fileuploader";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/Folder";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useMutation, useQuery } from "@tanstack/react-query";
import Labels from "../labels/Labels";
import LoadingButton from "../components/LoadingButton";

export default function UploadFile() {
  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const fileUploadRef = useRef(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [buttonToUploadEnabled, setButtonToUploadEnabled] = useState(false);
  const [resetKey, setResetKey] = useState(0); // Add a key to force re-render
  const [isProcessing, setIsProcessing] = useState(false); // Track base64 processing

  const handleFilesChange = (files) => {
    setFilesToUpload([...files]);
    if (files.length === 1) {
      setButtonToUploadEnabled(true);
    } else {
      setButtonToUploadEnabled(false);
    }
  };

  // Load the file list when the component mounts
  useEffect(() => {
    // Initial load of file list
    fileListQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  // Query to fetch file list
  const fileListQuery = useQuery(
    ["fileList"],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/misc/files`
      );
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const uploadFileMutation = useMutation(
    (fileData) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "json",
      };
      return HttpService.getAxiosClient().post(
        window.API_URL + `/misc/files/upload`,
        fileData,
        config
      );
    },
    {
      onSuccess: () => {
        // Reset the file upload control
        setFilesToUpload([]);
        setButtonToUploadEnabled(false);
        setResetKey((prevKey) => prevKey + 1); // Increment key to force re-render

        // Refresh the file list
        fileListQuery.refetch();

        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "uploadFile.uploadSuccess",
            "file uploaded successfully"
          ),
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const uploadFiles = async () => {
    if (!filesToUpload || filesToUpload.length === 0) {
      console.error("No files selected");
      return;
    }

    const file = filesToUpload[0]; // We're only handling one file at a time
    setIsProcessing(true);

    try {
      console.log(
        `Preparing to upload file: ${file.name}, type: ${file.type}, size: ${file.size} bytes using base64 encoding`
      );

      // Convert file to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          // Get the base64 string (remove the data URL prefix)
          const base64String = e.target.result.split(",")[1];
          resolve(base64String);
        };
        reader.onerror = (e) => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file); // This encodes as base64
      });

      // Create payload with file information and base64 content
      const payload = {
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
        base64Content: base64Data,
      };

      console.log(
        `File encoded as base64, uploading ${payload.fileName} (${Math.round(
          base64Data.length / 1024
        )} KB encoded)`
      );

      // Upload the file
      await uploadFileMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Error preparing file for upload:", error);
      setFeedback({
        open: true,
        type: "error",
        message: "Error preparing file for upload: " + error.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container maxWidth="xlg">
      <Grid container spacing={1.5} style={{ maxWidth: "80%" }}>
        <Grid item xs={12} md={4}>
          <img src={uploadImg} alt="upload" width="70%" loading="lazy" />
        </Grid>
        <Grid item xs={12} md={8}>
          <h1>{Labels.getLabel("uploadFile.title", "File Upload")}</h1>

          {/* File Uploader with Base64 Encoding */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              File Uploader
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Upload any type of file (images, PDFs, documents, etc.) - all
              files are handled securely using base64 encoding
            </Typography>

            <Fragment>
              <FileUpload
                key={resetKey} // Add key to force re-render
                multiFile={false}
                onFilesChange={handleFilesChange}
                title={Labels.getLabel(
                  "uploadFile.uploadFileTitle",
                  "Please upload the file"
                )}
                showPlaceholderImage={false}
                allowedExtensions={[
                  "csv",
                  "pdf",
                  "xlsx",
                  "xls",
                  "docx",
                  "doc",
                  "txt",
                  "png",
                  "jpg",
                  "jpeg",
                  "gif",
                  "bmp",
                  "zip",
                  "vnd.openxmlformats-officedocument.wordprocessingml.document",
                  "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  "vnd.openxmlformats-officedocument.presentationml.presentation",
                ]} // Allow all common file types
                maxUploadFiles={1}
                header={Labels.getLabel(
                  "uploadFile.dragAndDropHere",
                  "> DRAG AND DROP HERE <"
                )}
                onContextReady={(context) => {
                  fileUploadRef.current = context;
                }}
              />
              <LoadingButton
                loading={uploadFileMutation.isLoading || isProcessing}
                disabled={
                  !buttonToUploadEnabled ||
                  uploadFileMutation.isLoading ||
                  isProcessing
                }
                style={{
                  margin: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={(e) => uploadFiles(e)}
                variant="contained"
              >
                <CloudUploadIcon style={{ marginRight: "8px" }} />
                <span>
                  {isProcessing
                    ? "Processing file..."
                    : uploadFileMutation.isLoading
                    ? "Uploading..."
                    : Labels.getLabel("uploadFile.uploadButton", "Upload")}
                </span>
              </LoadingButton>

              {/* Add a note about base64 encoding */}
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                sx={{ mt: 1 }}
              >
                Files are securely encoded with base64 for reliable transfer of
                all file types.
              </Typography>
            </Fragment>
          </Paper>
        </Grid>
      </Grid>

      {/* File List Section */}
      <Grid
        container
        spacing={1.5}
        style={{ maxWidth: "80%", marginTop: "5px" }}
      >
        <Grid item xs={12} md={4}>
          {/* Keep the same column empty for alignment */}
        </Grid>
        <Grid item xs={12} md={8}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="h2">
              {Labels.getLabel("uploadFile.filesList", "Uploaded Files")}
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => fileListQuery.refetch()}
              disabled={fileListQuery.isLoading}
            >
              {Labels.getLabel("uploadFile.refresh", "Refresh")}
            </Button>
          </div>

          {fileListQuery.isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress />
            </div>
          ) : fileListQuery.error ? (
            <Alert severity="error">
              {Labels.getLabel(
                "uploadFile.errorLoadingFiles",
                "Error loading files"
              )}
            </Alert>
          ) : (
            <TableContainer component={Paper} style={{ marginTop: "10px" }}>
              <Table sx={{ minWidth: 650 }} aria-label="files table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {Labels.getLabel("uploadFile.fileName", "File Name")}
                    </TableCell>
                    <TableCell>
                      {Labels.getLabel("uploadFile.fileSize", "Size")}
                    </TableCell>
                    <TableCell>
                      {Labels.getLabel("uploadFile.fileType", "Type")}
                    </TableCell>
                    <TableCell>
                      {Labels.getLabel(
                        "uploadFile.filePermissions",
                        "Permissions"
                      )}
                    </TableCell>
                    <TableCell>
                      {Labels.getLabel(
                        "uploadFile.fileLastModified",
                        "Last Modified"
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fileListQuery.data && fileListQuery.data.length > 0 ? (
                    fileListQuery.data
                      .sort(
                        (a, b) =>
                          new Date(b.lastModified) - new Date(a.lastModified)
                      )
                      .map((file, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {file.directory ? (
                                <FolderIcon
                                  style={{
                                    marginRight: "8px",
                                    color: "#FFC107",
                                  }}
                                />
                              ) : (
                                <InsertDriveFileIcon
                                  style={{
                                    marginRight: "8px",
                                    color: "#2196F3",
                                  }}
                                />
                              )}
                              <Tooltip title={file.path}>
                                <span>{file.fileName}</span>
                              </Tooltip>
                            </div>
                          </TableCell>
                          <TableCell>
                            {file.directory
                              ? "-"
                              : file.size < 1024
                              ? `${file.size} B`
                              : file.size < 1024 * 1024
                              ? `${(file.size / 1024).toFixed(2)} KB`
                              : `${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                          </TableCell>
                          <TableCell>
                            {file.directory ? (
                              <Chip
                                label="Directory"
                                color="warning"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label={file.fileName
                                  .split(".")
                                  .pop()
                                  .toUpperCase()}
                                color="primary"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <code>{file.permissions}</code>
                          </TableCell>
                          <TableCell>
                            {new Date(file.lastModified).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {Labels.getLabel(
                          "uploadFile.noFiles",
                          "No files found"
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
